import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Feather, Loader2, Check, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { billingStatus } from "@/lib/api";
import { toast } from "sonner";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

const PLAN_LABELS = {
    thoughts: "Seer",
    single_dream: "Enchanter",
    dreamer_pro: "Magnus",
    aza: "Aza",
};
const planLabel = (p) => PLAN_LABELS[p] || "Your plan";

export default function BillingReturn() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", attempts: 0 });
    const runRef = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            nav("/");
            return;
        }
        const myRun = ++runRef.current;
        let errorRetries = 0;

        async function poll(attempt) {
            if (runRef.current !== myRun) return;
            if (attempt >= MAX_ATTEMPTS) {
                setState({ status: "timeout", attempts: attempt });
                return;
            }
            try {
                const res = await billingStatus(sessionId);
                if (runRef.current !== myRun) return;
                errorRetries = 0;
                if (res.payment_status === "paid") {
                    setState({ status: "paid", data: res, attempts: attempt });
                    toast.success("Welcome to " + planLabel(res.plan));
                    return;
                }
                if (res.status === "expired") {
                    setState({ status: "expired", attempts: attempt });
                    return;
                }
                setState({ status: "polling", attempts: attempt });
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
            } catch (e) {
                if (runRef.current !== myRun) return;
                if (errorRetries < 2) {
                    errorRetries += 1;
                    setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
                    return;
                }
                setState({ status: "error", error: e?.response?.data?.detail || "Error", attempts: attempt });
            }
        }
        poll(0);
        return () => {
            // Invalidate this run so its in-flight promises are ignored; the next
            // effect run will start a fresh poll loop.
        };
    }, [sessionId, nav]);

    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <Card className="max-w-md w-full p-8 text-center">
                <div className="flex items-center justify-center gap-2 font-heading text-2xl mb-4">
                    <Feather className="w-5 h-5 text-primary" /> Day Dreamer
                </div>
                {state.status === "polling" && (
                    <>
                        <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin mb-3" data-testid="billing-polling" />
                        <h2 className="font-heading text-2xl font-normal mb-2">Confirming your subscription…</h2>
                        <p className="text-sm text-muted-foreground">Checking with Stripe (attempt {state.attempts + 1}/{MAX_ATTEMPTS})</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <div className="w-12 h-12 mx-auto rounded-full bg-primary/15 flex items-center justify-center mb-3">
                            <Check className="w-6 h-6 text-primary" />
                        </div>
                        <h2 className="font-heading text-3xl font-normal mb-2" data-testid="billing-success">Thank you!</h2>
                        <p className="text-sm text-muted-foreground mb-1">
                            {planLabel(state.data.plan)} is now active.
                        </p>
                        <p className="text-xs text-muted-foreground mb-6">
                            {state.data.days} days of full access.
                        </p>
                        <Button data-testid="billing-continue" className="w-full" onClick={() => nav("/")}>
                            <Sparkles className="w-4 h-4" /> Open library
                        </Button>
                    </>
                )}
                {state.status === "timeout" && (
                    <>
                        <p className="text-sm text-muted-foreground mb-3">
                            Still processing… Stripe will email you when confirmed.
                        </p>
                        <Button onClick={() => nav("/")} variant="outline">Back to library</Button>
                    </>
                )}
                {state.status === "expired" && (
                    <>
                        <X className="w-8 h-8 mx-auto text-destructive mb-3" />
                        <p className="text-sm text-muted-foreground mb-3">Checkout session expired.</p>
                        <Button onClick={() => nav("/pricing")}>Try again</Button>
                    </>
                )}
                {state.status === "error" && (
                    <>
                        <X className="w-8 h-8 mx-auto text-destructive mb-3" />
                        <p className="text-sm text-muted-foreground mb-3">{state.error}</p>
                        <Button onClick={() => nav("/")}>Back to library</Button>
                    </>
                )}
            </Card>
        </div>
    );
}
